import React, { useEffect, useState } from "react";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { trackEvent } from "../../../services/GoogleAnalytics";
import { usePagesTranslation } from "../../locales";
import LetterWheel, { buildWheelTiles } from "../../puzzles/wordwheel/components/LetterWheel";
import ClueBanner from "../../puzzles/wordwheel/components/ClueBanner";
import { wwColors, wwPlayColorsLight } from "../../puzzles/wordwheel/theme";

const DEMO_WHEEL_TILES = buildWheelTiles(["C", "A", "T", "R"], "howto");

/** Mini crossword: CAT is found, ART is still hidden. */
const DEMO_GRID = [
    [
        { letter: "C", number: 1 },
        { letter: "A" },
        { letter: "T" },
        null,
    ],
    [
        null,
        { letter: "", number: 2 },
        { letter: "" },
        { letter: "" },
    ],
];

function DemoFrame({ children, maxWidth = 320 }) {
    return (
        <Box
            sx={{
                my: 2,
                mx: "auto",
                maxWidth,
                p: 2,
                borderRadius: 3,
                background: wwColors.playGradient,
                boxShadow: wwColors.shadowMd,
            }}
        >
            {children}
        </Box>
    );
}

function DemoGrid() {
    const colors = wwPlayColorsLight;
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "6px",
                width: "100%",
                maxWidth: 240,
                mx: "auto",
            }}
        >
            {DEMO_GRID.flat().map((cell, index) => {
                const revealed = Boolean(cell?.letter);
                return (
                    <Box
                        key={index}
                        sx={{
                            aspectRatio: "1 / 1",
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 1.25,
                            fontWeight: 800,
                            fontSize: "1.15rem",
                            color: revealed ? colors.successText : "transparent",
                            bgcolor: !cell
                                ? "transparent"
                                : revealed
                                    ? colors.successSoft
                                    : colors.gridCellHidden,
                            border: cell
                                ? `2px solid ${revealed ? "#bbf7d0" : colors.borderStrong}`
                                : "none",
                        }}
                    >
                        {cell?.number ? (
                            <Box
                                component="span"
                                sx={{
                                    position: "absolute",
                                    top: 2,
                                    left: 3,
                                    fontSize: "0.7rem",
                                    fontWeight: 800,
                                    color: "#064e3b",
                                    bgcolor: "rgba(255, 255, 255, 0.96)",
                                    borderRadius: "4px",
                                    px: 0.35,
                                    lineHeight: 1.3,
                                }}
                            >
                                {cell.number}
                            </Box>
                        ) : null}
                        {cell?.letter || ""}
                    </Box>
                );
            })}
        </Box>
    );
}

function DemoWheel() {
    const [selectedIndices, setSelectedIndices] = useState([0, 1, 2]);
    return (
        <Box sx={{ width: "100%", height: 240 }}>
            <LetterWheel
                tiles={DEMO_WHEEL_TILES}
                selectedIndices={selectedIndices}
                onSelectionChange={setSelectedIndices}
                onDragEnd={() => {}}
                colors={wwPlayColorsLight}
                fill
            />
        </Box>
    );
}

export default function HowToWordWheel() {
    const { t } = usePagesTranslation();

    useEffect(() => {
        trackEvent("entered", { name: "HowToWordWheel" });
    }, []);

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                {t("wordwheel.howto-title")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                {t("wordwheel.howto-intro", "Spell words on the wheel. Each word you find fills in on the grid.")}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t("wordwheel.howto-wheel-title", "The wheel")}
            </Typography>
            <DemoFrame>
                <DemoWheel />
            </DemoFrame>
            <Typography variant="body1" sx={{ mb: 2.5, lineHeight: 1.7 }}>
                {t(
                    "wordwheel.howto-wheel-body",
                    "The letters for this puzzle sit around the wheel. Drag from letter to letter to spell a word. A line follows your path so you can see which letters you have chosen. Let go to submit. A word needs at least three letters. Shuffle moves the letters to new places on the wheel if you want another look.",
                )}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t("wordwheel.howto-grid-title", "The grid")}
            </Typography>
            <DemoFrame maxWidth={360}>
                <DemoGrid />
                <Box sx={{ mt: 1.5 }}>
                    <ClueBanner
                        compact
                        hasClue
                        clueText="2. A painting or a drawing"
                        textColor={wwPlayColorsLight.textOnSurface}
                    />
                </Box>
            </DemoFrame>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                {t(
                    "wordwheel.howto-grid-body",
                    "The grid is the crossword you are filling. It starts blank. When a word from the wheel is accepted, its letters appear across or down in their places. A number marks where a word begins. Tap that cell to read the clue. The puzzle is done when every word on the grid is filled.",
                )}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
                <VisibilityOutlinedIcon sx={{ color: "#3BA666", mt: 0.3 }} />
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{t("wordwheel.credits-label")}</Typography>
                    <Typography>{t("wordwheel.howto-credits")}</Typography>
                </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
                <LightbulbOutlinedIcon sx={{ color: "#3BA666", mt: 0.3 }} />
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{t("wordwheel.coins-label")}</Typography>
                    <Typography>{t("wordwheel.howto-coins")}</Typography>
                </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t("wordwheel.howto-no-ads")}
            </Typography>
        </Container>
    );
}
